import React, { useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  ChartBarIcon,
  UserGroupIcon,
  ArrowTrendingUpIcon,
  LinkIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import { StarIcon } from '@heroicons/react/24/solid';

const CompetitiveAnalysis = () => {
  const [userDomain, setUserDomain] = useState('');
  const [competitors, setCompetitors] = useState([]);
  const [newCompetitor, setNewCompetitor] = useState('');
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedMetric, setSelectedMetric] = useState('traffic');

  const handleAddCompetitor = () => {
    if (newCompetitor && competitors.length < 5) {
      setCompetitors([...competitors, newCompetitor]);
      setNewCompetitor('');
    } else if (competitors.length >= 5) {
      toast.error('Maximum 5 competitors allowed');
    }
  };

  const removeCompetitor = (index) => {
    setCompetitors(competitors.filter((_, i) => i !== index));
  };

  const handleAnalyze = async () => {
    if (!userDomain) {
      toast.error('Please enter your domain');
      return;
    }
    if (competitors.length === 0) {
      toast.error('Please add at least one competitor');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post('/api/competitor/competitive-analysis', {
        domain: userDomain,
        competitors
      });
      setAnalysis(response.data);
      toast.success('Competitive analysis complete!');
    } catch (error) {
      // Show error to user
      toast.error('Failed to fetch competitive analysis. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const generateMockAnalysis = (yourDomain, comps) => {
    // Include your domain in the analysis
    const allDomains = [yourDomain, ...comps];
    
    return {
      overview: {
        yourDomain,
        yourPosition: comps.length > 2 ? Math.floor(comps.length / 2) + 1 : 2,
        totalCompetitors: comps.length,
        marketShare: 15,
        growthRate: 12
      },
      // Your domain metrics
      yourMetrics: {
        domain: yourDomain,
        metrics: {
          traffic: {
            organic: 250000,
            paid: 15000,
            trend: Array.from({ length: 12 }, () => Math.floor(Math.random() * 100))
          },
          keywords: {
            total: 3500,
            top10: 450,
            top3: 85
          },
          backlinks: {
            total: 25000,
            domains: 1200
          },
          content: {
            pages: 350,
            avgLength: 2100
          },
          authority: 72
        }
      },
      competitors: comps.map((comp, idx) => ({
        domain: comp,
        metrics: {
          traffic: {
            organic: Math.floor(Math.random() * 1000000) + 100000,
            paid: Math.floor(Math.random() * 50000),
            trend: Array.from({ length: 12 }, () => Math.floor(Math.random() * 100))
          },
          keywords: {
            total: Math.floor(Math.random() * 10000) + 1000,
            top10: Math.floor(Math.random() * 1000) + 100,
            top3: Math.floor(Math.random() * 100) + 10
          },
          backlinks: {
            total: Math.floor(Math.random() * 100000) + 10000,
            domains: Math.floor(Math.random() * 5000) + 500
          },
          content: {
            pages: Math.floor(Math.random() * 1000) + 100,
            avgLength: Math.floor(Math.random() * 1000) + 1500
          },
          authority: Math.floor(Math.random() * 40) + 60
        }
      })),
      gaps: {
        keyword: {
          missing: Math.floor(Math.random() * 5000) + 1000,
          opportunities: [
            { keyword: 'enterprise SEO tools', volume: 5400, difficulty: 65 },
            { keyword: 'competitive analysis software', volume: 3200, difficulty: 58 },
            { keyword: 'SEO competitor tracking', volume: 2100, difficulty: 45 },
            { keyword: 'backlink gap analysis', volume: 1800, difficulty: 52 },
            { keyword: 'content gap finder', volume: 1500, difficulty: 42 }
          ]
        },
        content: {
          missingTopics: [
            'Advanced link building strategies',
            'Enterprise SEO case studies',
            'Technical SEO automation',
            'International SEO guide',
            'E-commerce SEO tactics'
          ],
          contentTypes: ['Video tutorials', 'Infographics', 'Webinars', 'Tools', 'Templates']
        },
        backlink: {
          missingDomains: 342,
          linkableAssets: ['Research studies', 'Free tools', 'Industry reports', 'Expert guides']
        }
      },
      opportunities: [
        {
          type: 'content',
          title: 'Create comprehensive guides on underserved topics',
          impact: 'high',
          effort: 'medium',
          potentialTraffic: '+25,000 monthly visits'
        },
        {
          type: 'keywords',
          title: 'Target 500+ low-competition keywords',
          impact: 'high',
          effort: 'low',
          potentialTraffic: '+15,000 monthly visits'
        },
        {
          type: 'backlinks',
          title: 'Build links from 100+ competitor referring domains',
          impact: 'very high',
          effort: 'high',
          potentialTraffic: '+30% domain authority'
        }
      ]
    };
  };

  const MetricCard = ({ title, value, trend, icon: Icon }) => (
    <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-gray-600">{title}</span>
        <Icon className="h-5 w-5 text-gray-400" />
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      {trend && (
        <div className="flex items-center mt-2">
          <ArrowTrendingUpIcon className={`h-4 w-4 ${trend > 0 ? 'text-green-500' : 'text-red-500'}`} />
          <span className={`text-sm ml-1 ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
            {trend > 0 ? '+' : ''}{trend}%
          </span>
        </div>
      )}
    </div>
  );

  const CompetitorRow = ({ competitor, index }) => (
    <tr className="hover:bg-gray-50">
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <span className="text-lg font-semibold text-gray-400 mr-3">#{index + 1}</span>
          <div>
            <p className="text-sm font-medium text-gray-900">{competitor.domain}</p>
            <p className="text-xs text-gray-500">DA: {competitor.metrics.authority}</p>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <p className="text-sm text-gray-900">{competitor.metrics.traffic.organic.toLocaleString()}</p>
        <p className="text-xs text-gray-500">+{competitor.metrics.traffic.paid.toLocaleString()} paid</p>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <p className="text-sm text-gray-900">{competitor.metrics.keywords.total.toLocaleString()}</p>
        <p className="text-xs text-gray-500">{competitor.metrics.keywords.top10} in top 10</p>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <p className="text-sm text-gray-900">{competitor.metrics.backlinks.total.toLocaleString()}</p>
        <p className="text-xs text-gray-500">{competitor.metrics.backlinks.domains} domains</p>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <p className="text-sm text-gray-900">{competitor.metrics.content.pages}</p>
        <p className="text-xs text-gray-500">Avg: {competitor.metrics.content.avgLength} words</p>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          {[...Array(5)].map((_, i) => (
            <StarIcon
              key={i}
              className={`h-4 w-4 ${
                i < Math.floor(competitor.metrics.authority / 20) ? 'text-yellow-400' : 'text-gray-300'
              }`}
            />
          ))}
        </div>
      </td>
    </tr>
  );

  return (
    <>
      <div className="min-h-screen bg-gray-50">
        <div className="px-6 py-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <UserGroupIcon className="h-8 w-8 text-indigo-600 mr-3" />
                Competitive Analysis
              </h1>
              <p className="text-gray-600 mt-2">
                Track competitors, identify gaps, and discover opportunities
              </p>
            </div>
            {analysis && (
              <button
                onClick={() => {/* Export functionality */}}
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center"
              >
                <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                Export Report
              </button>
            )}
          </div>
        </div>

        {/* Domain and Competitor Input */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          {/* Your Domain */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold mb-3">Your Domain</h2>
            <input
              type="text"
              value={userDomain}
              onChange={(e) => setUserDomain(e.target.value)}
              placeholder="Enter your domain (e.g., yourdomain.com)"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45e09f] focus:border-transparent"
            />
          </div>
          
          <h2 className="text-lg font-semibold mb-4">Add Competitors (Max 5)</h2>
          <div className="flex gap-4 mb-4">
            <div className="flex-1">
              <input
                type="text"
                value={newCompetitor}
                onChange={(e) => setNewCompetitor(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddCompetitor()}
                placeholder="Enter competitor domain (e.g., competitor.com)"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45e09f] focus:border-transparent"
              />
            </div>
            <button
              onClick={handleAddCompetitor}
              className="px-6 py-3 bg-[#45e09f] text-[#212433] rounded-lg hover:bg-[#3ec989] flex items-center transition-colors">
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Add Competitor
            </button>
          </div>

          {/* Competitor List */}
          <div className="flex flex-wrap gap-2 mb-4">
            {competitors.map((comp, idx) => (
              <div
                key={idx}
                className="flex items-center px-3 py-2 bg-gray-100 rounded-lg"
              >
                <span className="text-sm font-medium text-gray-700">{comp}</span>
                <button
                  onClick={() => removeCompetitor(idx)}
                  className="ml-2 text-gray-500 hover:text-red-600"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>

          {competitors.length > 0 && (
            <button
              onClick={handleAnalyze}
              disabled={loading}
                className="px-6 py-3 bg-[#45e09f] text-[#212433] rounded-lg font-semibold hover:bg-[#3ec989] disabled:opacity-50 transition-colors"
            >
              {loading ? 'Analyzing...' : 'Analyze Competitors'}
            </button>
          )}
        </div>

        {/* Analysis Results */}
        {analysis && (
          <>
            {/* Overview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <MetricCard
                title="Your Market Position"
                value={`#${analysis.overview.yourPosition}`}
                trend={null}
                icon={ChartBarIcon}
              />
              <MetricCard
                title="Competitors Analyzed"
                value={analysis.overview.totalCompetitors}
                trend={null}
                icon={UserGroupIcon}
              />
              <MetricCard
                title="Market Share"
                value={`${analysis.overview.marketShare}%`}
                trend={analysis.overview.growthRate}
                icon={ChartBarIcon}
              />
              <MetricCard
                title="Growth Rate"
                value={`${analysis.overview.growthRate}%`}
                trend={null}
                icon={ArrowTrendingUpIcon}
              />
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-lg shadow-sm">
              <div className="border-b">
                <nav className="flex -mb-px">
                  {['overview', 'keywords', 'content', 'backlinks', 'gaps', 'opportunities'].map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`py-3 px-6 border-b-2 font-medium text-sm capitalize transition-colors ${
                        activeTab === tab
                          ? 'border-[#45e09f] text-[#45e09f]'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {tab}
                    </button>
                  ))}
                </nav>
              </div>

              <div className="p-6">
                {/* Overview Tab */}
                {activeTab === 'overview' && (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Competitor
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Traffic
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Keywords
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Backlinks
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Content
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Authority
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {/* Your Domain Row - Highlighted */}
                        {analysis.yourMetrics && (
                          <tr className="bg-[#45e09f]/10 border-2 border-[#45e09f]/30">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <span className="text-lg font-semibold text-[#45e09f] mr-3">YOU</span>
                                <div>
                                  <p className="text-sm font-medium text-gray-900">{analysis.yourMetrics.domain}</p>
                                  <p className="text-xs text-gray-500">DA: {analysis.yourMetrics.metrics.authority}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <p className="text-sm text-gray-900">{analysis.yourMetrics.metrics.traffic.organic.toLocaleString()}</p>
                              <p className="text-xs text-gray-500">+{analysis.yourMetrics.metrics.traffic.paid.toLocaleString()} paid</p>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <p className="text-sm text-gray-900">{analysis.yourMetrics.metrics.keywords.total.toLocaleString()}</p>
                              <p className="text-xs text-gray-500">{analysis.yourMetrics.metrics.keywords.top10} in top 10</p>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <p className="text-sm text-gray-900">{analysis.yourMetrics.metrics.backlinks.total.toLocaleString()}</p>
                              <p className="text-xs text-gray-500">{analysis.yourMetrics.metrics.backlinks.domains} domains</p>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <p className="text-sm text-gray-900">{analysis.yourMetrics.metrics.content.pages}</p>
                              <p className="text-xs text-gray-500">Avg: {analysis.yourMetrics.metrics.content.avgLength} words</p>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                {[...Array(5)].map((_, i) => (
                                  <StarIcon
                                    key={i}
                                    className={`h-4 w-4 ${
                                      i < Math.floor(analysis.yourMetrics.metrics.authority / 20) ? 'text-yellow-400' : 'text-gray-300'
                                    }`}
                                  />
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                        {/* Competitor Rows */}
                        {analysis.competitors.map((comp, idx) => (
                          <CompetitorRow key={idx} competitor={comp} index={idx} />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Keywords Tab */}
                {activeTab === 'keywords' && (
                  <div>
                    <div className="mb-6">
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <div className="flex">
                          <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mr-2" />
                          <div>
                            <h3 className="text-sm font-semibold text-yellow-900">
                              Keyword Gap Detected
                            </h3>
                            <p className="text-sm text-yellow-700 mt-1">
                              You're missing {analysis.gaps.keyword.missing} keywords that competitors rank for
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <h3 className="font-semibold text-gray-900 mb-3">Top Keyword Opportunities</h3>
                    <div className="space-y-3">
                      {analysis.gaps.keyword.opportunities.map((kw, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-medium text-gray-900">{kw.keyword}</p>
                            <p className="text-sm text-gray-500">Volume: {kw.volume.toLocaleString()}</p>
                          </div>
                          <div className="flex items-center space-x-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              kw.difficulty < 50 ? 'bg-green-100 text-green-700' :
                              kw.difficulty < 70 ? 'bg-yellow-100 text-yellow-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              Difficulty: {kw.difficulty}
                            </span>
                            <button className="text-[#45e09f] hover:text-[#3ec989] text-sm font-medium">
                              Target Keyword →
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Content Tab */}
                {activeTab === 'content' && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3">Missing Content Topics</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      {analysis.gaps.content.missingTopics.map((topic, idx) => (
                        <div key={idx} className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                          <CheckCircleIcon className="h-5 w-5 text-blue-600 mb-2" />
                          <p className="font-medium text-blue-900">{topic}</p>
                          <p className="text-sm text-blue-700 mt-1">High search demand, low competition</p>
                        </div>
                      ))}
                    </div>

                    <h3 className="font-semibold text-gray-900 mb-3">Content Type Gaps</h3>
                    <div className="flex flex-wrap gap-2">
                      {analysis.gaps.content.contentTypes.map((type, idx) => (
                        <span
                          key={idx}
                          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium"
                        >
                          {type}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Backlinks Tab */}
                {activeTab === 'backlinks' && (
                  <div>
                    <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-6">
                      <h3 className="font-semibold text-indigo-900 mb-2">Backlink Gap Analysis</h3>
                      <p className="text-indigo-700">
                        Competitors have backlinks from {analysis.gaps.backlink.missingDomains} domains that don't link to you
                      </p>
                    </div>

                    <h3 className="font-semibold text-gray-900 mb-3">Recommended Link Building Assets</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {analysis.gaps.backlink.linkableAssets.map((asset, idx) => (
                        <div key={idx} className="p-4 border border-gray-200 rounded-lg">
                          <LinkIcon className="h-5 w-5 text-indigo-600 mb-2" />
                          <p className="font-medium text-gray-900">{asset}</p>
                          <p className="text-sm text-gray-600 mt-1">High link potential</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Gaps Tab */}
                {activeTab === 'gaps' && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                        <h4 className="font-semibold text-red-900 mb-2">Keyword Gap</h4>
                        <p className="text-2xl font-bold text-red-700">{analysis.gaps.keyword.missing}</p>
                        <p className="text-sm text-red-600">Missing keywords</p>
                      </div>
                      <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                        <h4 className="font-semibold text-yellow-900 mb-2">Content Gap</h4>
                        <p className="text-2xl font-bold text-yellow-700">{analysis.gaps.content.missingTopics.length}</p>
                        <p className="text-sm text-yellow-600">Missing topics</p>
                      </div>
                      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                        <h4 className="font-semibold text-blue-900 mb-2">Backlink Gap</h4>
                        <p className="text-2xl font-bold text-blue-700">{analysis.gaps.backlink.missingDomains}</p>
                        <p className="text-sm text-blue-600">Missing domains</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Opportunities Tab */}
                {activeTab === 'opportunities' && (
                  <div className="space-y-4">
                    {analysis.opportunities.map((opp, idx) => (
                      <div key={idx} className="p-6 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-semibold text-gray-900 mb-2">{opp.title}</h3>
                            <div className="flex items-center space-x-4 text-sm">
                              <span className={`px-2 py-1 rounded ${
                                opp.impact === 'very high' ? 'bg-red-100 text-red-700' :
                                opp.impact === 'high' ? 'bg-orange-100 text-orange-700' :
                                'bg-yellow-100 text-yellow-700'
                              }`}>
                                Impact: {opp.impact}
                              </span>
                              <span className={`px-2 py-1 rounded ${
                                opp.effort === 'low' ? 'bg-green-100 text-green-700' :
                                opp.effort === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                Effort: {opp.effort}
                              </span>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-600">Potential:</p>
                            <p className="font-semibold text-green-600">{opp.potentialTraffic}</p>
                          </div>
                        </div>
                        <button className="mt-4 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium">
                          View Action Plan →
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Empty State */}
        {!analysis && !loading && (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <UserGroupIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Start Competitive Analysis</h3>
            <p className="text-gray-600 max-w-md mx-auto">
              Add up to 5 competitors to analyze their SEO strategies, identify gaps, and discover opportunities to outrank them.
            </p>
          </div>
        )}
        </div>
      </div>
    </>
  );
};

export default CompetitiveAnalysis;
